import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { Property } from '../../types/property';

interface PrivatePDFProps {
  property: Property;
  brandColor?: string;
}

export const PrivatePDF: React.FC<PrivatePDFProps> = ({ property, brandColor = '#0F766E' }) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      fontFamily: 'Helvetica',
      padding: 25,
      height: '100%',
    },
    confidentialHeader: {
      backgroundColor: '#FEE2E2',
      padding: 10,
      borderRadius: 6,
      marginBottom: 20,
      alignItems: 'center',
    },
    confidentialText: {
      color: '#DC2626',
      fontSize: 12,
      fontWeight: 'bold',
      textAlign: 'center',
    },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: brandColor,
      marginBottom: 15,
      textAlign: 'center',
    },
    section: {
      marginBottom: 15,
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 10,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
      paddingLeft: 8,
    },
    infoGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    infoRow: {
      flexDirection: 'row',
      marginBottom: 6,
      width: '100%',
    },
    infoLabel: {
      width: '35%',
      fontSize: 10,
      color: '#6B7280',
      fontWeight: 'bold',
    },
    infoValue: {
      width: '65%',
      fontSize: 10,
      color: '#111827',
    },
    priceBadge: {
      backgroundColor: `${brandColor}20`,
      paddingVertical: 4,
      paddingHorizontal: 10,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: brandColor,
      alignSelf: 'flex-end',
      marginLeft: 'auto',
    },
    priceText: {
      fontSize: 10,
      fontWeight: 'bold',
      color: brandColor,
    },
    footer: {
      marginTop: 20,
      textAlign: 'center',
      fontSize: 8,
      color: '#9CA3AF',
      borderTopWidth: 1,
      borderTopColor: '#E5E7EB',
      paddingTop: 10,
    },
  });

  const formatPrice = (price: number) => {
    return Math.floor(price || 0).toLocaleString('fr-FR').replace(/\s/g, ' ') + ' DA';
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  // Fonction pour obtenir le type de bien de manière robuste
  const getPropertyType = () => {
    // Priorité à property_category + property_subtype
    if (property.property_category) {
      return property.property_subtype && property.property_subtype !== 'Simple'
        ? `${property.property_category} ${property.property_subtype}`
        : property.property_category;
    }
    // Fallback sur property.type
    return property.type || 'Bien immobilier';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête confidentiel */}
        <View style={styles.confidentialHeader}>
          <Text style={styles.confidentialText}>DOCUMENT CONFIDENTIEL - USAGE INTERNE UNIQUEMENT</Text>
        </View>

        {/* Titre */}
        <Text style={styles.title}>
          {getPropertyType()}
        </Text>

        {/* Informations du propriétaire */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informations du propriétaire</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Nom :</Text>
              <Text style={styles.infoValue}>{property.owner?.name || property.owner_name || 'Non spécifié'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Téléphone :</Text>
              <Text style={styles.infoValue}>{property.owner?.phone || property.owner_phone || 'Non spécifié'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Email :</Text>
              <Text style={styles.infoValue}>{property.owner?.email || property.owner_email || 'Non spécifié'}</Text>
            </View>
          </View>
        </View>

        {/* Informations financières */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informations financières</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Prix net vendeur :</Text>
              <Text style={styles.infoValue}>{formatPrice(property.netPrice || property.net_price || 0)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Commission :</Text>
              <Text style={styles.infoValue}>{formatPrice(property.commission || 0)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Prix de vente :</Text>
              <View style={styles.priceBadge}>
                <Text style={styles.priceText}>{formatPrice(property.price || 0)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Informations du bien */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informations du bien</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Adresse :</Text>
              <Text style={styles.infoValue}>{property.address || 'Non spécifiée'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Surface :</Text>
              <Text style={styles.infoValue}>{Math.floor(property.features?.area || property.area || 0)} m²</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Pièces :</Text>
              <Text style={styles.infoValue}>{property.features?.rooms || property.rooms || 0}</Text>
            </View>
            {(property.features?.floor !== undefined || property.floor !== undefined) && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Étage :</Text>
                <Text style={styles.infoValue}>{property.features?.floor || property.floor || 'RDC'}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Suivi commercial */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Suivi commercial</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Agent en charge :</Text>
              <Text style={styles.infoValue}>{property.agent || property.created_by_name || 'Non assigné'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Date de création :</Text>
              <Text style={styles.infoValue}>{formatDate(property.createdAt || property.created_at)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Dernière mise à jour :</Text>
              <Text style={styles.infoValue}>{formatDate(property.updatedAt || property.updated_at)}</Text>
            </View>
          </View>
        </View>

        {/* Pied de page */}
        <Text style={styles.footer}>
          Fiche confidentielle • Document généré le {new Date().toLocaleDateString('fr-FR')} • Réf: {property.id}
        </Text>
      </Page>
    </Document>
  );
};
